import React, {useRef, useCallback} from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {useStores} from '../../../Hooks';
import {
    trainingAndFieldRoleEnum,
    contentCreatingRoleEnum,
    workspaceUsersEnum,
} from '../../../serverClientShared/sharedEnums';

const camelToSentence = (str = '') => {
    const spaced = str.replace(/([A-Z])/g, ' $1').trim();
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : '';
};

const formatRolePart = (role, noneValue) => {
    if (!role || role === noneValue) return null;
    if (role === trainingAndFieldRoleEnum.OPERATOR_3D) return 'Operator MR';
    return camelToSentence(role);
};

export const getRoleDisplayText = (trainingRole, contentRole, adminRole, translate) => {
    if (adminRole === workspaceUsersEnum.WORKSPACE_ADMIN) return translate('admin');

    const parts = [
        formatRolePart(trainingRole, trainingAndFieldRoleEnum.NONE),
        formatRolePart(contentRole, contentCreatingRoleEnum.NONE),
        formatRolePart(adminRole, workspaceUsersEnum.NONE),
    ].filter(Boolean);

    if (parts.length === 0) return translate('SpecifyRoles');
    if (parts.length === 1) return parts[0];
    return `${parts.length} roles`;
};

const RoleSelector = ({trainingRole, contentRole, adminRole, presetName, onRolesChange}) => {
    const {modalStore, generalStore} = useStores();
    const roleRef = useRef(null);

    const isPresetsEnabled = !!generalStore.domainSettings?.IsNewRolesSystemEnabled;

    const openRolePopup = useCallback(() => {
        const rect = roleRef.current.getBoundingClientRect();

        modalStore.chooseInitRoleState = {
            trainingAndField: new Set([trainingRole]),
            contentCreating: new Set([contentRole]),
            administration: new Set([adminRole]),
        };
        modalStore.chooseRolePopupPosition = {
            x: rect.left + window.scrollX,
            y: rect.bottom + window.scrollY,
        };
        modalStore.handleSaveRolePopup = (trainingRoles, contentRoles, adminRoles) => {
            onRolesChange({
                trainingAndFieldRole: trainingRoles?.[0] || trainingRole,
                contentCreationRole: contentRoles?.[0] || contentRole,
                administrationRole: adminRoles?.[0] || adminRole,
                rolePresetId: null,
                presetName: null,
            });
        };
        const config = {singleSelect: true, showAdministration: true};
        if (isPresetsEnabled) {
            config.showPresetActions = true;
            config.initialView = 'presets';
            config.onPresetAssign = (preset) => {
                onRolesChange({
                    trainingAndFieldRole: preset.TrainingAndFieldRole,
                    contentCreationRole: preset.ContentCreationRole,
                    administrationRole: preset.AdministrationRole,
                    rolePresetId: preset.RolePresetId,
                    presetName: preset.Name,
                });
            };
        }
        modalStore.chooseRolePopupConfig = config;
        modalStore.isChooseRolePopupOpen = true;
        modalStore.modalData.isBatch = false;
    }, [trainingRole, contentRole, adminRole, onRolesChange, modalStore, isPresetsEnabled]);

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-main-normal">Role</label>
            <div
                ref={roleRef}
                data-testid="invite-role-selector"
                className="flex items-center justify-between p-2 rounded-menu border border-panels-normal bg-windowsandmenus-normal cursor-pointer hover:bg-panels-normal hover:border-classic-button-normal"
                onClick={openRolePopup}
            >
                <span className="text-sm text-main-normal">
                    {presetName || getRoleDisplayText(trainingRole, contentRole, adminRole, generalStore.translate)}
                </span>
                <KeyboardArrowDownIcon style={{fontSize: 20}} className="text-main-light" />
            </div>
        </div>
    );
};

export default RoleSelector;
